import React from 'react'
import { FormField, FormReadonlyField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Heading5 } from '@/components/ui/typography'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

import {
  IntroducingBrokerCommissionPlanGroupDto,
  TradingAccountType,
  TradingAccountTypeName,
} from '../../../api/Trading-Accounts/Types/IntroducingBrokerCommissionPlanGroupDto'
import { truncateToTwoDecimalPlaces } from '../../../utils/string.utils'

interface AccountTypeValues {
  tradingAccountTypeId: number
  amount: number
  maxRebate: number
  currentAmount: number
}

type AddEditIntroducingBrokerCommissionPlanGroupDto = {
  [key in TradingAccountTypeName]: AccountTypeValues
}

export interface AddEditIntroducingBrokerCommissionPlanGroupDtoFormValues {
  tradingAccountTypeId: number
  amount: number
}

const accountTypes: { id: TradingAccountType; name: TradingAccountTypeName }[] = [
  { id: TradingAccountType.Pro, name: 'Pro' },
  { id: TradingAccountType.VIP, name: 'VIP' },
  { id: TradingAccountType.Classic, name: 'Classic' },
  { id: TradingAccountType.Raw, name: 'Raw' },
]

const IntroducingBrokerRebateFormModalUI = ({
  values,
  errors,
  handleSubmit,
  isSubmitting,
  onClose,
  data,
}: FormikProps<AddEditIntroducingBrokerCommissionPlanGroupDto> & OuterProps) => {
  const handleSaveCommission = (event?: React.SyntheticEvent) => {
    event?.preventDefault()
    handleSubmit()
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent size='lg' render={<Form onSubmit={handleSaveCommission} />}>
        <DialogHeader>
          <DialogTitle>{data?.id ? 'Edit IB Profile' : 'Add IB Profile'}</DialogTitle>
        </DialogHeader>

        {accountTypes.map((accountType) => (
          <div key={accountType.id} className='flex min-w-0 flex-col gap-3'>
            <Heading5>{accountType.name}</Heading5>
            <FormReadonlyField
              label='Max rebate available'
              value={values[accountType.name]?.maxRebate ?? ''}
            />
            <FormField
              name={`${accountType.name}.amount`}
              label={data?.name || 'Amount'}
              type='number'
              step='0.1'
              error={errors[accountType.name]?.amount}
            />
          </div>
        ))}

        <DialogFooter>
          <Button disabled={isSubmitting} type='button' variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button type='button' disabled={isSubmitting} onClick={handleSaveCommission}>
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface OuterProps {
  data?: IntroducingBrokerCommissionPlanGroupDto
  assignedCommissionPlans: IntroducingBrokerCommissionPlanGroupDto | undefined

  onSubmit(
    values: FormSubmitValues<AddEditIntroducingBrokerCommissionPlanGroupDtoFormValues[]>,
    id?: string
  ): Promise<void>

  onClose: () => void
}

function dataToSubmit(
  values: AddEditIntroducingBrokerCommissionPlanGroupDto
): FormSubmitValues<AddEditIntroducingBrokerCommissionPlanGroupDtoFormValues[]> {
  return accountTypes.map((accountType) => {
    const typeName = accountType.name
    return {
      tradingAccountTypeId: values[typeName].tradingAccountTypeId,
      amount: truncateToTwoDecimalPlaces(values[typeName].amount),
    }
  })
}

export const IntroducingBrokerRebateFormModal = withFormik<
  OuterProps,
  AddEditIntroducingBrokerCommissionPlanGroupDto
>({
  mapPropsToValues: (props) => {
    const initialValues: AddEditIntroducingBrokerCommissionPlanGroupDto =
      {} as AddEditIntroducingBrokerCommissionPlanGroupDto

    accountTypes.forEach((accountType) => {
      const accountData = props.data?.commissionPlans.find(
        (x) => x.tradingAccountType.id === accountType.id
      ) ?? {
        tradingAccountType: { id: accountType.id },
        amount: 0,
      }

      initialValues[accountType.name] = {
        tradingAccountTypeId: accountData.tradingAccountType.id ?? accountType.id,
        amount: accountData.amount ?? 0,
        maxRebate:
          props.assignedCommissionPlans?.commissionPlans.find(
            (x) => x.tradingAccountType.id === accountType.id
          )?.amount ?? 0,
        currentAmount: accountData.amount ?? 0,
      }
    })

    return initialValues
  },
  handleSubmit: async (values, { props }) => {
    await props.onSubmit(dataToSubmit(values), props.data?.id)
  },
  validate: (values) => {
    const errors: FormikErrors<AddEditIntroducingBrokerCommissionPlanGroupDto> = {}

    const hasMoreThanTwoDecimalPlaces = (value: number) => {
      const decimalPart = value.toString().split('.')[1]
      return decimalPart && decimalPart.length > 2
    }

    accountTypes.forEach((accountType) => {
      const typeName = accountType.name
      const value = values[typeName]
      if (!value) return

      if (hasMoreThanTwoDecimalPlaces(value.amount)) {
        if (!errors[typeName]) errors[typeName] = {}
        errors[typeName].amount = 'Amount should have only up to 2 decimal places'
      }
      if (value.amount > value.maxRebate) {
        if (!errors[typeName]) errors[typeName] = {}
        errors[typeName].amount = 'You cannot distribute more than the Max Rebate Available'
      }
      if (value.amount < value.currentAmount) {
        if (!errors[typeName]) errors[typeName] = {}
        errors[typeName].amount = 'You cannot distribute less than the existing Rebates'
      }
    })

    return errors
  },
})(IntroducingBrokerRebateFormModalUI)
